import { useNavigate } from 'react-router-dom';
import { default as Box } from '@mui/material/Box';
import { MenuLink, MenuLinkTitle } from 'components/MenuLink';
import { PATHS } from 'config/paths';
import { ATS_TRACKER_VARIANT } from 'modules/applications';
import { getTrans } from 'modules/i18n';
import { ModalName, useModal } from 'modules/modal';
import {
  FeatureFlag,
  useFeatureToggle,
  useFeatureVariant
} from 'modules/unleash';

export const Profile = ({
  withTopOffset = true
}: {
  withTopOffset?: boolean;
}) => {
  const navigate = useNavigate();
  const { open } = useModal();

  const { isEnabled: isAssignmentManagementEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_ASSIGNMENT_MANAGEMENT_ENABLED
  );

  const { isEnabled: isUsercentricEnabled } = useFeatureToggle(
    FeatureFlag.IS_USERCENTRICS_UI_ENABLED
  );

  const { isEnabled: isGeolocationEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_GEOLOCATION_ENABLED
  );

  const { isEnabled: isPushEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_PUSH_ENABLED
  );

  const { isEnabled: isCvBuilderEnabled } = useFeatureToggle(
    FeatureFlag.PRO_IN_APP_CV_CREATION
  );

  const { variant: atsVariant } = useFeatureVariant(FeatureFlag.PRO_ATS);
  const isApplicationsTrackerEnabled = atsVariant === ATS_TRACKER_VARIANT;
  const { isEnabled: isAvailabilityEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_AVAILABILITY
  );

  return (
    <Box sx={withTopOffset ? { pt: 8, mt: 10 } : { pt: 1 }}>
      {isCvBuilderEnabled && (
        <MenuLink
          onClick={() => navigate(PATHS.cvBuilder)}
          dataTestId="my-cv-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.myCv')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      <MenuLink
        onClick={() => navigate(PATHS.languageSettings)}
        dataTestId="language-settings-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.languageSettings')}
        </MenuLinkTitle>
      </MenuLink>

      {isApplicationsTrackerEnabled && (
        <MenuLink
          onClick={() => navigate(PATHS.applications)}
          dataTestId="applications-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.applications')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      {isAssignmentManagementEnabled && (
        <MenuLink
          onClick={() => navigate(PATHS.assignments)}
          dataTestId="assignments-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.assignments')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      {isAvailabilityEnabled && (
        <MenuLink
          onClick={() => navigate(PATHS.availability)}
          dataTestId="availability-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.availability')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      {isPushEnabled && (
        <MenuLink
          onClick={() => navigate(PATHS.notificationSettings)}
          dataTestId="notification-settings-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.notificationSettings')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      <MenuLink
        onClick={() => window.open(getTrans('url.helpCenter'), '_blank')}
        dataTestId="help-center-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.helpCenter')}
        </MenuLinkTitle>
      </MenuLink>
      <MenuLink
        onClick={() => navigate(PATHS.contactZenjob)}
        dataTestId="contact-zenjob-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.contactZenjob')}
        </MenuLinkTitle>
      </MenuLink>

      {(isUsercentricEnabled || isGeolocationEnabled) && (
        <MenuLink
          onClick={() => navigate(PATHS.privacySettings)}
          dataTestId="privacy-settings-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.privacySettings')}
          </MenuLinkTitle>
        </MenuLink>
      )}
      {!isUsercentricEnabled && (
        <MenuLink
          onClick={() => window.open(getTrans('url.privacyPolicy'), '_blank')}
          dataTestId="privacy-policy-link"
          withArrow={true}
        >
          <MenuLinkTitle>
            {getTrans('profilePage.linkLabel.privacyPolicy')}
          </MenuLinkTitle>
        </MenuLink>
      )}

      <MenuLink
        onClick={() => window.open(getTrans('url.zenjobWebsite'), '_blank')}
        dataTestId="zenjob-website-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.zenjobWebsite')}
        </MenuLinkTitle>
      </MenuLink>
      <MenuLink
        onClick={() =>
          window.open(getTrans('url.employmentAgencyLeaflet'), '_blank')
        }
        dataTestId="leaflet-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.leaflet')}
        </MenuLinkTitle>
      </MenuLink>
      <MenuLink
        onClick={() => navigate(PATHS.deleteProfile)}
        dataTestId="delete-profile-link"
        withArrow={true}
      >
        <MenuLinkTitle>
          {getTrans('profilePage.linkLabel.deleteProfile')}
        </MenuLinkTitle>
      </MenuLink>
      <MenuLink onClick={() => open(ModalName.logout)} dataTestId="logout-link">
        <MenuLinkTitle sx={{ color: 'error.main' }}>
          {getTrans('general.logout')}
        </MenuLinkTitle>
      </MenuLink>
    </Box>
  );
};
